/* eslint-disable */
/**
 * CohortRetentionBIPanel.jsx - Klient-Cohort Retention heatmap
 * Endpoint: GET /api/os/bi/cohort-retention
 */
import React, { useState, useEffect, useCallback } from 'react';

function fmtMaaned(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('da-DK', { month: 'short', year: '2-digit' });
}

function retentionFarve(pct) {
  if (pct == null) return 'rgba(255,255,255,0.04)';
  const intensity = Math.min(1, pct / 100);
  const r = Math.round(59 + (8 - 59) * intensity);
  const g = Math.round(130 + (12 - 130) * intensity);
  const b = Math.round(246 + (30 - 246) * intensity);
  return 'rgba(' + r + ',' + g + ',' + b + ',' + (0.15 + intensity * 0.75) + ')';
}

export default function CohortRetentionBIPanel() {
  const [data, setData] = useState(null);
  const [fejl, setFejl] = useState(null);
  const [loader, setLoader] = useState(true);

  const hent = useCallback(async () => {
    try {
      const r = await fetch('/api/os/bi/cohort-retention', { credentials: 'include' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setData(d);
      setFejl(null);
    } catch (err) {
      setFejl(err.message);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => { hent(); }, [hent]);

  const matrix = React.useMemo(() => {
    if (!data || !data.cohort_sizes || !data.aktivitet) return [];

    const sizes = {};
    data.cohort_sizes.forEach(r => {
      sizes[new Date(r.maaned).toISOString().slice(0, 7)] = r.size;
    });

    const cohorts = Object.keys(sizes).sort();
    const maxMdr = 6;

    return cohorts.map(cohortMaaned => {
      const cohortDate = new Date(cohortMaaned + '-01');
      const size = sizes[cohortMaaned] || 0;
      const celler = [];

      for (let mdr = 0; mdr <= maxMdr; mdr++) {
        const aktivitetsDato = new Date(cohortDate);
        aktivitetsDato.setMonth(aktivitetsDato.getMonth() + mdr);
        const aktivitetsMaaned = aktivitetsDato.toISOString().slice(0, 7);

        const match = data.aktivitet.find(a => {
          const cm = new Date(a.cohort_maaned).toISOString().slice(0, 7);
          const am = new Date(a.aktivitets_maaned).toISOString().slice(0, 7);
          return cm === cohortMaaned && am === aktivitetsMaaned;
        });

        const aktive = match ? match.aktive_klienter : 0;
        const pct = size > 0 ? Math.round((aktive / size) * 100) : null;
        celler.push({ mdr, aktive, pct, har_data: !!match });
      }
      return { cohortMaaned, size, celler };
    });
  }, [data]);

  const erSparsom = matrix.length < 2;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>Cohort Retention</div>
        <div style={{ fontSize: 13, color: '#7a8fa6', marginTop: 3 }}>
          Klientaktivitet maaned for maaned efter oprettelse (6 mdr)
        </div>
      </div>

      {loader && <div style={{ textAlign: 'center', padding: 40, color: '#7a8fa6' }}>Henter...</div>}
      {fejl && !loader && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '12px 16px', color: '#f87171', fontSize: 13 }}>
          Fejl: {fejl}
        </div>
      )}

      {!loader && !fejl && matrix.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#7a8fa6', fontSize: 13 }}>
          Ingen data endnu
        </div>
      )}

      {!loader && !fejl && matrix.length > 0 && (
        <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ minWidth: 360 }}>
            <div style={{ display: 'flex', gap: 2, marginBottom: 4, marginLeft: 80 }}>
              {Array.from({ length: 7 }, (_, i) => (
                <div key={i} style={{
                  width: 44, textAlign: 'center', fontSize: 10, color: '#7a8fa6', fontWeight: 600,
                }}>
                  {i === 0 ? 'M0' : '+' + i + 'md'}
                </div>
              ))}
            </div>

            {matrix.map(row => (
              <div key={row.cohortMaaned} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
                <div style={{ width: 76, flexShrink: 0, fontSize: 11, color: '#7a8fa6', textAlign: 'right', paddingRight: 8 }}>
                  <div>{fmtMaaned(row.cohortMaaned)}</div>
                  <div style={{ fontSize: 10, color: 'rgba(122,143,166,0.6)' }}>n={row.size}</div>
                </div>
                {row.celler.map(celle => (
                  <div
                    key={celle.mdr}
                    title={celle.pct != null ? (celle.pct + '% (' + celle.aktive + ' aktive)') : 'Ingen data'}
                    style={{
                      width: 44, height: 36, borderRadius: 4,
                      background: retentionFarve(celle.pct),
                      border: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      color: celle.pct != null && celle.pct > 30 ? '#fff' : '#9db3c8',
                      cursor: 'default',
                    }}
                  >
                    {celle.pct != null ? celle.pct + '%' : '—'}
                  </div>
                ))}
              </div>
            ))}

            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#7a8fa6', flexWrap: 'wrap' }}>
              <span>Lav</span>
              {[0, 25, 50, 75, 100].map(v => (
                <div key={v} style={{
                  width: 20, height: 14, borderRadius: 3,
                  background: retentionFarve(v),
                  border: '1px solid rgba(255,255,255,0.06)',
                }} />
              ))}
              <span>Hoj</span>
            </div>
          </div>
        </div>
      )}

      {erSparsom && !loader && !fejl && (
        <div style={{
          marginTop: 12, padding: '8px 12px', fontSize: 12, color: '#f59e0b',
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 6,
        }}>
          Kraever 3-6 mdr data for fuld statistisk styrke.
        </div>
      )}
    </div>
  );
}
